import { FC, memo, useEffect, useState } from 'react';
import { Dropdown, SkeletonLoader } from '@geneui/components';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
// Import Services
import PredefinedHttpService from 'services/http/predefined';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Constants
import { l } from 'constants/common';
import { useFormik } from 'formik';
import { isEmpty, noop } from 'lodash';

interface Props {
    initialDataValues: FormValues;
    getUpdatedValues: (data: FormValues) => void;
    getIsFormValid: (data: boolean) => void;
}

const mapToLabelValue = (array, labelKey, valueKey) => {
    // TODO: Art move to common helpers or smth
    return array.map((item) => {
        return { label: item[labelKey], value: item[valueKey] };
    });
};

interface FormValues {
    predefined: {
        regions: number[];
        categories: number[];
    };
}

const EmptyFormValues: FormValues = {
    predefined: {
        regions: [],
        categories: [],
    },
};

const getValidationSchema = (t, isPredefined) =>
    Yup.object({
        predefined: Yup.object({
            regions: isPredefined
                ? Yup.array()
                      .min(1, t(l.CannotBeEmpty, { fieldName: t(l.Regions) }))
                      .required(t(l.CannotBeEmpty, { fieldName: t(l.Regions) }))
                : Yup.array().notRequired(),

            categories: isPredefined
                ? Yup.array()
                      .min(1, t(l.CannotBeEmpty, { fieldName: t(l.Categories) }))
                      .required(t(l.CannotBeEmpty, { fieldName: t(l.Categories) }))
                : Yup.array().notRequired(),
        }),
    });

const PredefinedTemplatePreSaveModalBodyContent: FC<Props> = ({
    initialDataValues,
    getUpdatedValues,
    getIsFormValid,
}) => {
    const { t } = useTranslation();
    const { doGetRequest } = useRequest();

    const [regionsData, setRegionsData] = useState([]);
    const [isLoadingRegions, setIsLoadingRegions] = useState(false);

    const [categoriesData, setCategoriesData] = useState([]);
    const [isLoadingCategories, setIsLoadingCategories] = useState(false);

    const { getCategoriesRequest, getRegionsRequest } = useCancelableRequests({
        getCategoriesRequest: PredefinedHttpService.getCategories,
        getRegionsRequest: PredefinedHttpService.getRegions,
    });

    const initialValues: FormValues = initialDataValues ?? EmptyFormValues;

    const formik = useFormik<FormValues>({
        initialValues,
        validationSchema: () => getValidationSchema(t, true),
        validateOnMount: true,
        onSubmit: noop,
    });

    const { values, errors, setFieldValue } = formik;

    const getCategories = () => {
        setIsLoadingCategories(true);
        doGetRequest(getCategoriesRequest.request, {
            successCallback: (data) => {
                setCategoriesData(mapToLabelValue(data, 'Name', 'PredefinedCategoryId'));
            },
        }).finally(() => setIsLoadingCategories(false));
    };

    const getRegions = () => {
        setIsLoadingRegions(true);
        doGetRequest(getRegionsRequest.request, {
            successCallback: (data) => {
                setRegionsData(mapToLabelValue(data, 'Name', 'PredefinedRegionId'));
            },
        }).finally(() => setIsLoadingRegions(false));
    };

    useEffect(() => {
        getUpdatedValues(values);
    }, [values, getUpdatedValues]);

    useEffect(() => {
        getIsFormValid(isEmpty(errors));
    }, [errors, getIsFormValid]);

    const init = () => {
        // if (isPredefined) {
        getRegions();
        getCategories();
        // }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', rowGap: '3rem', margin: '3rem 0' }}>
            <SkeletonLoader isBusy={isLoadingRegions}>
                <Dropdown
                    isMultiSelect={true}
                    label={t(l.Regions)}
                    placeholder={t(l.Regions)}
                    labelAppearance="swap"
                    data={regionsData}
                    value={values.predefined.regions}
                    onChange={(e) =>
                        setFieldValue(
                            'predefined.regions',
                            e.map((selectedOption) => selectedOption.value),
                        )
                    }
                    isLoading={isLoadingRegions}
                    isValid={!errors.predefined?.regions}
                    errorText={errors.predefined?.regions}
                />
            </SkeletonLoader>
            <SkeletonLoader isBusy={isLoadingCategories}>
                <Dropdown
                    isMultiSelect={true}
                    label={t(l.Categories)}
                    placeholder={t(l.Categories)}
                    labelAppearance="swap"
                    data={categoriesData}
                    value={values.predefined.categories}
                    onChange={(e) =>
                        setFieldValue(
                            'predefined.categories',
                            e.map((selectedOption) => selectedOption.value),
                        )
                    }
                    isLoading={isLoadingCategories}
                    isValid={!errors.predefined?.categories}
                    errorText={errors.predefined?.categories}
                />
            </SkeletonLoader>
        </div>
    );
};

export default memo(PredefinedTemplatePreSaveModalBodyContent);
